import {
  DocumentAttribute,
  DocumentReferenceBase,
  DocumentReferenceEntityType,
} from '@qtek/shared/models';

/**
 * Normalized document-reference state, shared by all 6 `*DocumentReference`
 * stores.
 *
 * Keyed by the reference row's own `_id` rather than held as a flat array:
 * merge-by-identity, `remove`, and duplicate suppression all fall out of the
 * shape instead of being re-derived by every code path. Ordering is derived
 * (see {@link orderReferences}), not positional — the previous `items[]`
 * relied on server array order, which nothing guarantees.
 */
export interface DocumentReferenceState<T extends DocumentReferenceBase> {
  /** `reference._id` -> reference. The single source of truth. */
  referenceMap: Record<string, T>;
  /** Number of loads in flight. `isLoading` derives from it. */
  pending: number;
  /** Bumped when data actually changed, so serving-API URLs can cache-bust. */
  cacheBuster: number;
}

/**
 * Argument accepted by `load`. `attribute` takes one value or several.
 * Single-reference lookups don't go through this — use `store.fetchById(id)`.
 */
export interface DocumentReferenceQuery {
  attribute?: DocumentAttribute | readonly DocumentAttribute[];
  entityId?: string;
  /**
   * Bypasses both the in-flight dedupe and the `_uts` staleness check on
   * reconcile. Needed when the caller's *role* changed rather than the data —
   * the server derives per-row `_scopes` from the session, not the request,
   * so a role change returns different `_scopes` on rows whose `_uts` never
   * moved. Never part of the query key (see {@link selectorKey}).
   */
  force?: boolean;
}

/** Argument accepted by the scoped read helpers. */
export interface DocumentReferenceSelector {
  attribute?: DocumentAttribute | readonly DocumentAttribute[];
  entityId?: string;
}

/** A `DocumentReferenceQuery` with its attributes sorted and a stable key. */
export interface NormalizedDocumentReferenceQuery {
  /** Canonical serialization — both the dedup key and the reconcile key. */
  key: string;
  attributes: readonly DocumentAttribute[];
  entityId?: string;
  /** See {@link DocumentReferenceQuery.force}. Deliberately excluded from `key`. */
  force: boolean;
}

export function createInitialDocumentReferenceState<
  T extends DocumentReferenceBase,
>(): DocumentReferenceState<T> {
  return { referenceMap: {}, pending: 0, cacheBuster: 0 };
}

/**
 * Sorted + de-duplicated, so `[A, B]` and `[B, A, B]` produce the same query
 * key and therefore share one in-flight request and one reconcile scope.
 */
export function toAttributeList(
  attribute?: DocumentAttribute | readonly DocumentAttribute[]
): readonly DocumentAttribute[] {
  if (attribute == null) {
    return [];
  }

  const list: readonly DocumentAttribute[] = Array.isArray(attribute)
    ? (attribute as readonly DocumentAttribute[])
    : [attribute as DocumentAttribute];

  return [...new Set(list)].sort();
}

const KEY_SEPARATOR = '|';

export function selectorKey(selector: DocumentReferenceSelector): string {
  return `${selector.entityId ?? ''}${KEY_SEPARATOR}${toAttributeList(
    selector.attribute
  ).join(',')}`;
}

export function normalizeQuery(
  query: DocumentReferenceQuery
): NormalizedDocumentReferenceQuery {
  return {
    key: selectorKey(query),
    attributes: toAttributeList(query.attribute),
    entityId: query.entityId,
    force: query.force ?? false,
  };
}

/**
 * Audit timestamps arrive as strings whose format isn't pinned by the spec —
 * epoch millis would sort wrongly under a plain string compare (`'9' > '10'`),
 * so parse numerically first and fall back to `Date.parse`.
 */
function timeValue(timestamp: string | undefined): number {
  if (!timestamp) {
    return 0;
  }

  const numeric = Number(timestamp);
  if (Number.isFinite(numeric)) {
    return numeric;
  }

  const parsed = Date.parse(timestamp);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Oldest first, tie-broken by `_id` so the order is total and stable. Readers
 * that want "the current one" (logo, avatar, main image) take the last entry.
 */
export function orderReferences<T extends DocumentReferenceBase>(
  referenceMap: Record<string, T>
): readonly T[] {
  return Object.values(referenceMap).sort((a, b) => {
    const delta = timeValue(a._cts) - timeValue(b._cts);
    if (delta !== 0) {
      return delta;
    }
    return a._id < b._id ? -1 : a._id > b._id ? 1 : 0;
  });
}

export function filterReferences<T extends DocumentReferenceBase>(
  references: readonly T[],
  selector: DocumentReferenceSelector
): readonly T[] {
  const attributes = toAttributeList(selector.attribute);
  const { entityId } = selector;

  if (!attributes.length && !entityId) {
    return references;
  }

  return references.filter(
    reference =>
      (!entityId || reference.entityId === entityId) &&
      (!attributes.length || attributes.includes(reference.attribute))
  );
}

/**
 * `entityType` isn't on the wire (none of the 6 responses include it) — it's
 * implicit in which endpoint was queried, so the store stamps it on.
 */
export function stampEntityType<T extends DocumentReferenceBase>(
  references: readonly T[],
  entityType: DocumentReferenceEntityType
): readonly T[] {
  return references.map(reference => ({ ...reference, entityType }) as T);
}

export interface ReconcileResult<T extends DocumentReferenceBase> {
  referenceMap: Record<string, T>;
  /** Ids this response returned — the eviction scope for the next reconcile. */
  ids: readonly string[];
  /** False when the response was byte-identical to what we already had. */
  changed: boolean;
}

/**
 * A reference row's own `_uts` does not move when only its nested `document`
 * changes — the backend saves the `Document` row alone (a classify through
 * `ItemDocumentReferenceEntityTask.update`, or the async `DocumentClassifier`),
 * leaving the reference row clean and its `@LastModifiedDate` unfired. So
 * `document._uts` has to be part of the freshness check too, or a reload
 * silently drops every nested change (`documentType`, `virusScanStatus`,
 * extraction progress, ...) whenever the reference row itself is untouched.
 */
function isStale<T extends DocumentReferenceBase>(
  previous: T,
  incoming: T
): boolean {
  return (
    previous._uts !== incoming._uts ||
    previous.document?._uts !== incoming.document?._uts
  );
}

/**
 * Merges a list response into the map and evicts what the server dropped.
 *
 * The eviction scope is `previousIds` — the ids *this same query key* returned
 * last time — not a predicate over the query. Two properties follow, and they
 * are why this is preferred over matching references against the query scope:
 *
 * - A query can never evict outside its own scope, structurally. There is no
 *   `inScope()` predicate to get wrong, so a `COMPANY_LOGO` load provably
 *   cannot touch `PERSONAL_IMAGE` rows.
 * - A locally committed upload can never be evicted by a list request that was
 *   issued before it. A just-uploaded reference was in no previous response, so
 *   it is not an eviction candidate — no sequence numbers or pending-write
 *   ledger needed to protect it.
 *
 * References whose `_uts` — and nested `document._uts` (see {@link isStale})
 * — are unchanged keep their previous object identity, so downstream
 * `computed`/`effect` consumers don't fire on a no-op reload.
 *
 * `force` skips the staleness check and replaces every incoming row
 * unconditionally — for a reload whose only change is the caller's own
 * `_scopes` projection (see {@link DocumentReferenceQuery.force}), which
 * `_uts` never reflects.
 */
export function reconcileReferences<T extends DocumentReferenceBase>(
  referenceMap: Record<string, T>,
  previousIds: readonly string[],
  incoming: readonly T[],
  force = false
): ReconcileResult<T> {
  const incomingIds = new Set(incoming.map(reference => reference._id));
  const next = { ...referenceMap };
  let changed = false;

  for (const id of previousIds) {
    if (!incomingIds.has(id) && next[id]) {
      delete next[id];
      changed = true;
    }
  }

  for (const reference of incoming) {
    const previous = next[reference._id];
    if (force || !previous || isStale(previous, reference)) {
      next[reference._id] = reference;
      changed = true;
    }
  }

  return {
    referenceMap: changed ? next : referenceMap,
    ids: [...incomingIds],
    changed,
  };
}

/**
 * Full replace of a single reference (upload commit, single-reference
 * GET/PUT) — correct because all three responses are the complete row, not
 * a partial projection (confirmed for PUT against the backend).
 */
export function applyReferenceUpsert<T extends DocumentReferenceBase>(
  state: DocumentReferenceState<T>,
  reference: T
): Partial<DocumentReferenceState<T>> {
  return {
    referenceMap: { ...state.referenceMap, [reference._id]: reference },
    cacheBuster: state.cacheBuster + 1,
  };
}

/** Removal of a single reference by its own `_id`. */
export function applyReferenceRemoval<T extends DocumentReferenceBase>(
  state: DocumentReferenceState<T>,
  referenceId: string
): Partial<DocumentReferenceState<T>> {
  if (!state.referenceMap[referenceId]) {
    return {};
  }

  const referenceMap = { ...state.referenceMap };
  delete referenceMap[referenceId];

  return { referenceMap, cacheBuster: state.cacheBuster + 1 };
}

export interface ScopeEvictionResult<T extends DocumentReferenceBase> {
  state: Partial<DocumentReferenceState<T>>;
  /** Ids actually dropped — the caller prunes its id-keyed caches with these. */
  evictedIds: readonly string[];
}

/**
 * Local-only removal of every cached reference matching `selector`. Unlike
 * `reconcileReferences` this issues no request and has no server truth behind
 * it — it exists so a consumer that is going away (data-room tab teardown) can
 * hand back the memory it caused this root-provided store to hold.
 *
 * An empty selector is a no-op, not a full wipe: the store is shared by 6
 * entities' worth of consumers, and a stray `evict({})` must not be able to
 * blank it.
 */
export function applyScopeEviction<T extends DocumentReferenceBase>(
  state: DocumentReferenceState<T>,
  selector: DocumentReferenceSelector
): ScopeEvictionResult<T> {
  if (!selector.entityId && !toAttributeList(selector.attribute).length) {
    return { state: {}, evictedIds: [] };
  }

  const matches = filterReferences(Object.values(state.referenceMap), selector);
  if (!matches.length) {
    return { state: {}, evictedIds: [] };
  }

  const referenceMap = { ...state.referenceMap };
  const evictedIds = matches.map(reference => reference._id);
  evictedIds.forEach(id => delete referenceMap[id]);

  return {
    state: { referenceMap, cacheBuster: state.cacheBuster + 1 },
    evictedIds,
  };
}
